import { useEffect, useState, useMemo } from "react";
import { Container, Text, Spacer, Grid, Card, Row, Col, Loading, Image } from "@nextui-org/react";
import { getWorkshops } from "../../config/workshopsApi";
import { useNavigate } from "react-router-dom";

interface Workshop {
  id: string;
  title: string;
  description: string;
  date: string;
  startTime: string;
  targetDuration: number; // in minutes
  instructorName: string;
  eventCoverUrl: string;
  status?: 'draft' | 'published';
}

const getWorkshopTimeStatus = (date: string, startTime: string, targetDuration: number): 'Upcoming' | 'Live' | 'Completed' => {
  // Combine date and time
  const startDateTimeString = `${date.split('T')[0]}T${startTime}`;
  const start = new Date(startDateTimeString).getTime();

  // If parsing fails, default to Upcoming
  if (isNaN(start)) return 'Upcoming';

  const durationMs = targetDuration * 60 * 1000;
  const end = start + durationMs;
  const now = Date.now();

  if (start > now) return 'Upcoming';
  if (start <= now && end > now) return 'Live';
  return 'Completed';
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchWorkshops = async () => {
      setIsLoading(true);
      try {
        const data = await getWorkshops();
        setWorkshops(Array.isArray(data) ? data : []); // Ensure array if data is null/undefined or object
      } catch (error) {
        console.error("Error fetching workshops:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkshops();
  }, []);

  const upcomingWorkshops = useMemo(() => {
    return workshops.filter((workshop) => {
      return getWorkshopTimeStatus(workshop.date, workshop.startTime, workshop.targetDuration) === 'Upcoming';
    });
  }, [workshops]);

  return (
    <Container md>
      <Spacer y={1} />
      <Text h2>Dashboard</Text>
      <Text size="$lg" color="gray">Upcoming Workshops</Text>
      <Spacer y={1} />

      {isLoading ? (
        <Loading type="points" />
      ) : (
        <Grid.Container gap={2} justify="flex-start">
          {upcomingWorkshops.length === 0 ? (
            <Text>No upcoming workshops found.</Text>
          ) : (
            upcomingWorkshops.map((workshop) => (
              <Grid xs={12} sm={6} md={4} key={workshop.id}>
                <Card
                  isPressable
                  isHoverable
                  variant="bordered"
                  onPress={() => navigate(`/workshop/${workshop.id}/monitoring`)}
                >
                  <Card.Body css={{ padding: 0 }}>
                    <Image
                      src={workshop.eventCoverUrl || "https://via.placeholder.com/400x200?text=No+Image"}
                      objectFit="cover"
                      width="100%"
                      height={140}
                      alt={workshop.title}
                    />
                  </Card.Body>
                  <Card.Footer css={{ justifyItems: "flex-start" }}>
                    <Row wrap="wrap" justify="space-between" align="center">
                      <Col>
                        <Text b>{workshop.title}</Text>
                        <Text css={{ color: "$accents7", fontWeight: "$semibold", fontSize: "$sm" }}>
                          {workshop.date} | {workshop.startTime}
                        </Text>
                        <Text css={{ color: "$accents7", fontSize: "$xs" }}>
                          {workshop.instructorName}
                        </Text>
                      </Col>
                    </Row>
                  </Card.Footer>
                </Card>
              </Grid>
            ))
          )}
        </Grid.Container>
      )}
    </Container>
  );
};

export default DashboardPage;
